/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  ListItemButton,
  Box,
} from '@mui/material';
import React from 'react';
import { usePluginsTranslation } from '../../../utils/i18n';
import type { DebugLogEntry, PluginCatalogEntry } from '../pluginDebugTypes';
import type { SelectedInstance } from '../types';

interface SidebarProps {
  logEntries: DebugLogEntry[];
  onSelect: (selection: SelectedInstance) => void;
  plugins: PluginCatalogEntry[];
  selected: SelectedInstance;
}

export const Sidebar: React.FC<SidebarProps> = ({
  logEntries,
  onSelect,
  plugins,
  selected,
}) => {
  const { t } = usePluginsTranslation();
  const countForInstance = (id: string): number =>
    logEntries.filter((entry) => entry.instanceId === id).length;

  return (
    <Box data-testid="plugin-debug-sidebar">
      {plugins.length === 0 ? (
        <Typography
          sx={{ color: 'text.secondary', padding: 2 }}
          variant="body2"
        >
          {t('no-plugins-registered')}
        </Typography>
      ) : (
        <List dense disablePadding>
          {plugins.map((plugin) => {
            const isPluginSelected = selected?.pluginId === plugin.pluginId;
            const firstInstanceId = plugin.instanceIds[0];
            const canSelectPlugin =
              Boolean(firstInstanceId) || isPluginSelected;

            return (
              <React.Fragment key={plugin.pluginId}>
                <ListItem
                  disablePadding
                  sx={{
                    bgcolor: 'grey.50',
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <ListItemButton
                    data-testid={`plugin-sidebar-plugin-${plugin.pluginId}`}
                    onClick={() => {
                      if (isPluginSelected) {
                        onSelect(null);
                        return;
                      }

                      if (firstInstanceId) {
                        onSelect({
                          instanceId: firstInstanceId,
                          pluginId: plugin.pluginId,
                        });
                      }
                    }}
                    selected={isPluginSelected}
                    sx={{
                      '&.Mui-selected .MuiListItemText-primary': {
                        color: 'primary.main',
                        fontWeight: 600,
                      },
                      alignItems: 'flex-start',
                      cursor: canSelectPlugin ? 'pointer' : 'default',
                    }}
                  >
                    <ListItemText
                      primary={plugin.name}
                      secondary={
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          {!!plugin.type && (
                            <Chip
                              label={plugin.type}
                              size="small"
                              sx={{ fontSize: 10, height: 18 }}
                            />
                          )}
                          {plugin.scopes.map((scope) => (
                            <Chip
                              color="info"
                              key={scope}
                              label={scope}
                              size="small"
                              sx={{ fontSize: 10, height: 18 }}
                              variant="outlined"
                            />
                          ))}
                          {!!plugin.version && (
                            <Typography
                              color="text.secondary"
                              component="span"
                              variant="caption"
                            >
                              {t('plugin-version', { version: plugin.version })}
                            </Typography>
                          )}
                        </Box>
                      }
                      slotProps={{ secondary: { component: 'div' } }}
                    />
                    <Chip
                      label={String(plugin.instanceIds.length)}
                      size="small"
                      variant="outlined"
                    />
                  </ListItemButton>
                </ListItem>
                {plugin.instanceIds.map((id) => (
                  <ListItemButton
                    data-testid={`plugin-sidebar-instance-${id}`}
                    key={id}
                    onClick={() =>
                      onSelect(
                        selected?.instanceId === id
                          ? null
                          : { instanceId: id, pluginId: plugin.pluginId },
                      )
                    }
                    selected={selected?.instanceId === id}
                    sx={{ pl: 4 }}
                  >
                    <ListItemText
                      primary={id}
                      slotProps={{
                        primary: { fontFamily: 'monospace', variant: 'body2' },
                      }}
                    />
                    <Chip
                      label={String(countForInstance(id))}
                      size="small"
                      sx={{ fontSize: 10, height: 18 }}
                      variant="outlined"
                    />
                  </ListItemButton>
                ))}
              </React.Fragment>
            );
          })}
        </List>
      )}
    </Box>
  );
};
